
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CheckSquare, Users, Calendar, List } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useNavigate } from "react-router-dom";

const TeacherDashboard = () => {
  const navigate = useNavigate();
  
  // Mock data
  const todaysHelpers = [
    { name: "Maria Wong", avatar: "https://i.pravatar.cc/150?u=maria" },
    { name: "David Kim", avatar: "https://i.pravatar.cc/150?u=david" },
  ];
  
  const upcomingTasks = [
    { id: "task1", title: "Review weekly lesson plan", dueDate: "Today", priority: "high" },
    { id: "task2", title: "Order art supplies", dueDate: "Tomorrow", priority: "medium" },
    { id: "task3", title: "Schedule parent meeting", dueDate: "May 16", priority: "low" },
  ];
  
  const absences = [
    { child: "Emma Johnson", date: "Today", reason: "Doctor appointment" },
    { child: "Lucas Miller", date: "Tomorrow", reason: "Family travel" },
  ];
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold tracking-tight">Teacher Dashboard</h1>
        <div className="flex gap-2">
          <Button onClick={() => navigate("/schedule")}>Manage Schedule</Button>
        </div>
      </div>
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Today's Parent Helpers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{todaysHelpers.length}</div>
            <div className="flex -space-x-2 mt-3">
              {todaysHelpers.map((helper, i) => (
                <Avatar key={i} className="border-2 border-background">
                  <AvatarImage src={helper.avatar} />
                  <AvatarFallback>{helper.name.charAt(0)}</AvatarFallback>
                </Avatar>
              ))}
            </div>
            <div className="mt-4 space-y-2">
              {todaysHelpers.map((helper, i) => (
                <div key={i} className="flex justify-between items-center text-sm">
                  <span>{helper.name}</span>
                  <Badge variant="outline">Helper</Badge>
                </div>
              ))}
            </div>
          </CardContent>
          <CardFooter>
            <Button variant="outline" size="sm" className="w-full" onClick={() => navigate("/schedule")}>
              View Full Schedule
            </Button>
          </CardFooter>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Tasks</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{upcomingTasks.length}</div>
            <p className="text-xs text-muted-foreground">
              Tasks needing attention
            </p>
            <div className="mt-4 space-y-2">
              {upcomingTasks.map((task) => (
                <div key={task.id} className="flex justify-between items-center text-sm">
                  <div className="flex items-center gap-2">
                    <CheckSquare className="h-4 w-4 text-muted-foreground" />
                    <span>{task.title}</span>
                  </div>
                  <Badge variant={
                    task.priority === "high" ? "destructive" : 
                    task.priority === "medium" ? "secondary" : 
                    "outline"
                  } className="text-xs">
                    {task.priority}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
          <CardFooter>
            <Button variant="outline" size="sm" className="w-full" onClick={() => navigate("/tasks")}>
              Manage Tasks
            </Button>
          </CardFooter>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Upcoming Absences</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{absences.length}</div>
            <p className="text-xs text-muted-foreground">
              Reported absences
            </p>
            <div className="mt-4 space-y-2">
              {absences.map((absence, i) => (
                <div key={i} className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-sm">{absence.child}</span>
                    <span className="text-xs text-muted-foreground">{absence.date}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{absence.reason}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      
      <div>
        <h2 className="text-lg font-semibold mb-4">Classroom Management</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Weekly Activities</CardTitle>
              <CardDescription>Planned activities for this week</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-medium">Monday</h3>
                    <p className="text-sm text-muted-foreground">Nature Walk & Art</p>
                  </div>
                  <Badge>Completed</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-medium">Tuesday</h3>
                    <p className="text-sm text-muted-foreground">Music & Movement</p>
                  </div>
                  <Badge>Today</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-medium">Wednesday</h3>
                    <p className="text-sm text-muted-foreground">Science Exploration</p>
                  </div>
                  <Badge variant="outline">Upcoming</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-medium">Thursday</h3>
                    <p className="text-sm text-muted-foreground">Storytelling & Drama</p>
                  </div>
                  <Badge variant="outline">Upcoming</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-medium">Friday</h3>
                    <p className="text-sm text-muted-foreground">Free Play & Review</p>
                  </div>
                  <Badge variant="outline">Upcoming</Badge>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button className="w-full">Edit Activity Plan</Button>
            </CardFooter>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Parent Participation Stats</CardTitle>
              <CardDescription>Monthly participation overview</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Workday Attendance</span>
                  <span>90%</span>
                </div>
                <Progress value={90} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Task Completion</span>
                  <span>75%</span>
                </div>
                <Progress value={75} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Meeting Attendance</span>
                  <span>85%</span>
                </div>
                <Progress value={85} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Volunteer Hours</span>
                  <span>95%</span>
                </div>
                <Progress value={95} className="h-2" />
              </div>
            </CardContent>
            <CardFooter>
              <Button variant="outline" className="w-full">View Detailed Report</Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TeacherDashboard;
